import {
  Criteria,
  CriteriaCategory,
  CriteriaDetails,
} from 'concepts/Criteria/framework/CriteriaFramework';

const normalise = (line: string) => line.trim().toLowerCase();

// Union-merge a single side (include or exclude) of a category: server lines
// first, then any local lines the server does not already have. Comparison is
// case-insensitive and whitespace-insensitive; the surviving copy keeps its
// original casing.
const mergeLines = (server: string[] = [], local: string[] = []): string[] => {
  const seen = new Set(server.map(normalise));
  const merged = [...server];

  local.forEach((line) => {
    const key = normalise(line);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(line);
    }
  });

  return merged;
};

// Appends a later blob's lines onto the merged list: content lines dedupe
// against what is already there, whilst blank lines are formatting rather than
// content, kept only where they still precede novel content so paragraph
// breaks survive the merge without identical blobs growing trailing blanks.
const appendBlobLines = (merged: string[], lines: string[]): string[] => {
  const seen = new Set(merged.map(normalise));

  lines.forEach((line, index) => {
    if (normalise(line) === '') {
      const next = lines
        .slice(index + 1)
        .find((later) => normalise(later) !== '');
      if (next && !seen.has(normalise(next))) merged.push(line);
      return;
    }

    if (!seen.has(normalise(line))) {
      seen.add(normalise(line));
      merged.push(line);
    }
  });

  return merged;
};

// Other stores its whole textarea as one item (see valueAsArray in
// CriteriaFieldsets), so item-wise merging would keep each session's blob as a
// separate item. Fold all items (server's first) into one line list and rejoin:
// the first blob passes through verbatim, later blobs' lines dedupe against it,
// and a side already corrupted into several items collapses back to one.
const mergeOtherSide = (
  server: string[] = [],
  local: string[] = []
): string[] => {
  const lines = [...server, ...local]
    .filter((item) => item !== '')
    .reduce<string[]>(
      (merged, item) =>
        merged.length
          ? appendBlobLines(merged, item.split('\n'))
          : item.split('\n'),
      []
    );

  return lines.length ? [lines.join('\n')] : [];
};

const mergeDetails = (
  server: CriteriaDetails | undefined,
  local: CriteriaDetails | undefined,
  mergeSide: (server?: string[], local?: string[]) => string[]
): CriteriaDetails => ({
  include: mergeSide(server?.include, local?.include),
  exclude: mergeSide(server?.exclude, local?.exclude),
});

/**
 * Union-merge two sets of criteria on a save conflict (PIER-882). For every
 * category present in either source, and for both the include and exclude
 * sides, keep the server lines first and append any local lines not already
 * present on the server. Duplicates are collapsed case- and
 * whitespace-insensitively, keeping the surviving copy's original casing.
 * Other merges the same way but line-wise within its single textarea item.
 */
export const mergeCriteria = (
  server: Criteria = {},
  local: Criteria = {}
): Criteria => {
  const keys = new Set<CriteriaCategory>([
    ...(Object.keys(server) as CriteriaCategory[]),
    ...(Object.keys(local) as CriteriaCategory[]),
  ]);

  const merged: Criteria = {};
  keys.forEach((key) => {
    merged[key] = mergeDetails(
      server[key],
      local[key],
      key === 'other' ? mergeOtherSide : mergeLines
    );
  });

  return merged;
};
